import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { isQueryReady } from "src/api/axiosClient/utils";
import { UnitSchema } from "../ssq/useGetOneSsqVersion";
import { ALLOWED_CHART_TYPES } from "src/utils/chart";
import { useTranslation } from "react-i18next";

export const getOneScoreApiKey = "getScore" as const;

const ScoreVariationFormulaSchema = z.object({
    formula: z.string(),
    position: z.number(),
});

const ScoreVariationSchema = z.object({
    id: z.number(),
    label: z.string(),
    unit: UnitSchema.optional(),
    formulas: ScoreVariationFormulaSchema.array(),
});

export const useGetOneScore = (scoreId: number) => {
    const { i18n } = useTranslation();
    const locale = i18n.resolvedLanguage || "en";
    const query = useQuery({
        queryKey: [getOneScoreApiKey, scoreId, locale],
        cacheTime: 0,
        queryFn: async () => {
            return await sureApiCall.get(`/api/scores/${scoreId}`, {
                headers: { "Accept-Language": locale },
            });
        },
        select: (response) => {
            const score = response.data;
            return zodParseWithLog(score, ScoreSchema, getOneScoreApiKey);
        },
        enabled: !!scoreId && scoreId > 0,
    });

    const isReady = isQueryReady(query, true);

    return {
        score: query.data,
        isReady,
        ...query,
    };
};

const BaseScoreSchema = z.object({
    id: z.number(),
    title: z.string(),
    code: z.string().nullable().optional(),
    type: z.string(),
    description: z.string().nullable().optional(),
    displayMode: z.string().optional(),
    datavizType: z.enum(ALLOWED_CHART_TYPES),
    isActionPlanAvailable: z.boolean(),
    isTargetAvailable: z.boolean(),
    hasBaseline: z.boolean().default(true),
    unit: UnitSchema.optional(),
    label: z.string().optional(),
    precision: z.number().optional(),
    improvement: z.enum(["increase", "decrease"]),
    aggregationType: z.enum(["sum", "average"]).optional(),
    variations: ScoreVariationSchema.array(),
    isChildOfEveryMainScore: z.boolean().optional(),
    eligibleEntityTypes: z.string().array().optional(),
});

export const ScoreSchema = BaseScoreSchema.extend({
    children: z.lazy(() => BaseScoreSchema.array()).optional(),
    parent: z.lazy(() => BaseScoreSchema.optional()),
});
